// ###### IMPORTS
import React, { useState, useEffect, useMemo } from 'react';
import { XMarkIcon, PencilSquareIcon } from '@heroicons/react/24/solid';
import { toast } from 'react-hot-toast';
import { updateAsset } from '../../viewServices/assets_api';
import { catalogLocationNames } from '../../viewServices/asset_location_catalog';
import { useAssetsData } from '../../../moduleServices/assets_data_context';
import SpinnerLoad from 'views/finance/moduleComponents/SpinnerLoad';

// ###### COMPONENT
const ModalAssetEdit = ({ selectedAsset, onClose, onSuccess }) => {
  const { locations, categories, loading: assetsDataLoading } = useAssetsData();
  const [isEditing, setIsEditing] = useState(false);
  const [editedAsset, setEditedAsset] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (selectedAsset) {
      const assetType = selectedAsset.asset_type || 'tradeable';
      const tradeable = assetType === 'tradeable';
      const isUpd = tradeable && (selectedAsset.is_updated ?? false);
      setEditedAsset({
        asset_name: selectedAsset.asset_name || '',
        asset_code: selectedAsset.asset_code || '',
        category_code: selectedAsset.category_code || '',
        location_name: selectedAsset.location_name || '',
        asset_type: assetType,
        external_ticker: tradeable ? (selectedAsset.external_ticker || '') : '',
        is_updated: isUpd,
        is_active: selectedAsset.is_active ?? true,
        update_mode: isUpd ? (selectedAsset.update_mode || 'not_applied') : 'not_applied',
        asset_counter: selectedAsset.asset_counter != null ? selectedAsset.asset_counter : '',
      });
      setIsEditing(false);
    }
  }, [selectedAsset]);

  const loadingRefs = assetsDataLoading && categories.length === 0;
  const locationNamesCatalog = useMemo(() => catalogLocationNames(locations), [locations]);
  const locationLegacyOption = useMemo(() => {
    const cur = editedAsset?.location_name?.trim() ?? '';
    if (!cur || locationNamesCatalog.includes(cur)) return null;
    return cur;
  }, [editedAsset?.location_name, locationNamesCatalog]);

  if (!selectedAsset || !editedAsset) return null;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'is_updated') {
      const checked = e.target.checked;
      setEditedAsset((prev) => ({
        ...prev,
        is_updated: checked,
        ...(!checked ? { update_mode: 'not_applied', external_ticker: '' } : {}),
      }));
      return;
    }
    if (name === 'is_active') {
      setEditedAsset((prev) => ({ ...prev, [name]: e.target.checked }));
      return;
    }
    if (name === 'asset_counter') {
      setEditedAsset((prev) => ({ ...prev, [name]: value === '' ? '' : value }));
    } else {
      setEditedAsset((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editedAsset.asset_name?.trim() || !editedAsset.asset_code?.trim()) {
      toast.error('Asset name and code are required.');
      return;
    }
    try {
      setSubmitting(true);
      const payload = {
        asset_name: editedAsset.asset_name.trim(),
        asset_code: editedAsset.asset_code.trim(),
        category_code: editedAsset.category_code?.trim() || '',
        location_name: editedAsset.location_name?.trim() || null,
        asset_type: editedAsset.asset_type || 'tradeable',
        external_ticker:
          editedAsset.asset_type === 'tradeable' && editedAsset.is_updated
            ? (editedAsset.external_ticker?.trim() || null)
            : null,
        is_updated: editedAsset.asset_type === 'tradeable' && Boolean(editedAsset.is_updated),
        is_active: Boolean(editedAsset.is_active),
        update_mode:
          editedAsset.asset_type === 'tradeable' && editedAsset.is_updated
            ? (editedAsset.update_mode || 'not_applied')
            : 'not_applied',
        asset_counter: editedAsset.asset_counter === '' ? null : (parseInt(editedAsset.asset_counter, 10) || null),
      };
      await updateAsset(selectedAsset.id, payload);
      toast.success('Asset updated.');
      onSuccess?.();
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.asset_code?.[0] || err.message || 'Failed to update asset.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white p-4 md:p-6 rounded-2xl shadow w-80 md:w-96 relative max-h-[90vh] overflow-y-auto">
        {submitting && (
          <div className="absolute inset-0 flex items-center justify-center z-10 bg-white/90 rounded-2xl">
            <div className="bg-white rounded-xl shadow-lg p-4 border border-gray-200">
              <SpinnerLoad />
            </div>
          </div>
        )}
        <div className="absolute top-2 right-2 flex gap-2">
          {!isEditing && (
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              disabled={submitting}
              className={`text-gray-500 ${submitting ? 'opacity-50 cursor-not-allowed' : 'hover:text-gray-700'}`}
              title="Edit"
            >
              <PencilSquareIcon className="h-5 w-5" />
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className={`text-gray-500 ${submitting ? 'opacity-50 cursor-not-allowed' : 'hover:text-gray-700'}`}
            title="Close"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <h2 className="text-lg font-semibold mb-4 pr-10">
          {isEditing ? 'Edit asset' : 'Asset details'}
        </h2>

        <form className="space-y-4" onSubmit={handleUpdate}>
          <div className="flex items-end gap-3">
            <div className="flex-1 min-w-0">
              <label className="block text-xs text-gray-500 mb-0.5">Name</label>
              <input
                type="text"
                name="asset_name"
                value={editedAsset.asset_name}
                onChange={handleInputChange}
                placeholder="Asset name"
                className="w-full px-2 py-1.5 border rounded-md text-sm"
                disabled={!isEditing || submitting}
                required
              />
            </div>
            <label className="flex items-center gap-2 cursor-pointer shrink-0 pb-1.5">
              <input
                type="checkbox"
                name="is_active"
                checked={editedAsset.is_active}
                onChange={handleInputChange}
                disabled={!isEditing || submitting}
                className="h-3.5 w-3.5 accent-green-600"
              />
              <span className="text-sm text-gray-700">Active</span>
            </label>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-0.5">Category &amp; code</label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                name="category_code"
                value={editedAsset.category_code}
                readOnly
                className="flex-1 min-w-0 px-2 py-1.5 border rounded-md text-sm bg-gray-50 text-gray-600"
                disabled
              />
              <input
                type="text"
                name="asset_counter"
                value={editedAsset.asset_counter === '' ? '' : editedAsset.asset_counter}
                readOnly
                className="shrink-0 w-12 max-w-[48px] px-0.5 py-1.5 border rounded-md text-sm bg-gray-50 text-gray-600 text-center"
                disabled
                title={String(editedAsset.asset_counter === '' ? '' : editedAsset.asset_counter)}
              />
            </div>
            <input
              type="text"
              name="asset_code"
              value={editedAsset.asset_code}
              readOnly
              className="mt-1.5 w-full px-2 py-1.5 border rounded-md text-sm bg-gray-50 text-gray-600 font-mono"
              disabled
            />
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1">Type</label>
            <div className="bg-gray-100 rounded-full p-0.5 inline-flex">
              {[
                { value: 'deposit', label: 'Deposit' },
                { value: 'tradeable', label: 'Tradeable' },
              ].map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  disabled={!isEditing || submitting}
                  onClick={() =>
                    setEditedAsset((prev) => ({
                      ...prev,
                      asset_type: value,
                      ...(value === 'deposit'
                        ? { is_updated: false, update_mode: 'not_applied', external_ticker: '' }
                        : {}),
                    }))
                  }
                  className={`px-3 py-1 text-xs font-medium transition-all ${
                    value === 'deposit' ? 'rounded-l-full' : 'rounded-r-full'
                  } ${
                    editedAsset.asset_type === value
                      ? 'text-white bg-green-500'
                      : 'text-gray-700 hover:bg-gray-200'
                  } ${(!isEditing || submitting) ? 'opacity-60 cursor-not-allowed' : ''}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-0.5">Location</label>
            <select
              name="location_name"
              value={editedAsset.location_name}
              onChange={handleInputChange}
              className="w-full px-2 py-1.5 border rounded-md text-sm bg-white"
              disabled={!isEditing || submitting || loadingRefs}
            >
              <option value="">{loadingRefs ? 'Loading…' : '— None —'}</option>
              {locationLegacyOption ? (
                <option value={locationLegacyOption}>
                  {locationLegacyOption} (not in catalog)
                </option>
              ) : null}
              {locationNamesCatalog.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
            {isEditing ? (
              <p className="mt-1 text-[10px] text-gray-400 leading-snug">
                Values come from Location Catalog (Manage). Stored as plain text on the asset.
              </p>
            ) : null}
          </div>
          {editedAsset.asset_type === 'tradeable' ? (
            <div>
              <label className="flex items-center gap-2 cursor-pointer mb-1">
                <input
                  type="checkbox"
                  name="is_updated"
                  checked={editedAsset.is_updated}
                  onChange={handleInputChange}
                  disabled={!isEditing || submitting}
                  className="h-3.5 w-3.5 accent-green-600"
                />
                <span className="text-sm text-gray-700">Update price from market</span>
              </label>
              <label className="block text-xs text-gray-500 mb-0.5">Update mode</label>
              <select
                name="update_mode"
                value={editedAsset.is_updated ? editedAsset.update_mode : 'not_applied'}
                onChange={handleInputChange}
                disabled={!isEditing || submitting || !editedAsset.is_updated}
                className="w-full px-2 py-1.5 border rounded-md text-sm bg-white mb-2"
              >
                <option value="not_applied">Not Applied</option>
                <option value="manual">Manual</option>
                <option value="api_yahoo_ibovespa">API Yahoo (Ibovespa)</option>
                <option value="api_yahoo_crypto">API Yahoo (Crypto)</option>
              </select>
              <label className="block text-xs text-gray-500 mb-0.5">External ticker (B3)</label>
              <input
                type="text"
                name="external_ticker"
                value={editedAsset.external_ticker}
                onChange={handleInputChange}
                placeholder="e.g. ABEV3"
                className="w-full px-2 py-1.5 border rounded-md text-sm font-mono"
                disabled={!isEditing || submitting || !editedAsset.is_updated}
              />
            </div>
          ) : null}
          {isEditing && (
            <div className="flex gap-2 pt-2">
              <button
                type="submit"
                disabled={submitting}
                className={`flex-1 h-10 text-white rounded-2xl ${submitting ? 'bg-green-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`}
              >
                {submitting ? 'Saving…' : 'Save'}
              </button>
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="px-4 h-10 rounded-2xl border border-gray-300 text-gray-700 hover:bg-gray-100"
              >
                Cancel
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default ModalAssetEdit;
